import {
  Banknote,
  Layers3,
  BarChart3,
  Building2,
  CircleDollarSign,
  ClipboardList,
  FileCheck,
  Landmark,
  MapPin,
  Layers,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Moon,
  Newspaper,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldAlert,
  Stamp,
  Star,
  UserCircle,
  Users,
  Wallet,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useEnterpriseOptional } from "@/contexts/EnterpriseContext";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { ThemePreferenceMenuItems } from "@/components/shared/ThemePreferenceMenuItems";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { useMyProfileAvatarObjectUrl } from "@/hooks/useMyProfileAvatarObjectUrl";
import { enterpriseLogoUrl, enterpriseDarkLogoUrl } from "@/services/platformEnterprisesService";

function getDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  displayName: string | null | undefined,
  email: string
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (displayName?.trim()) return displayName;
  return email;
}

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isSidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const enterpriseCtx = useEnterpriseOptional();
  const enterprise = enterpriseCtx?.enterprise ?? null;
  const isEnterprise = enterprise != null;
  const isEnterpriseAdmin = enterpriseCtx?.myRole === "enterprise_admin";
  const isPlatform =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  const isAccountant = user?.role === "accountant" || user?.isAccountant === true;
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);

  // Close the sidebar whenever the URL changes on narrow viewports so the
  // mobile drawer doesn't stay covering the page after navigation. Skip on
  // the first render so the initial route doesn't trigger a close.
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;
    previousPathnameRef.current = location.pathname;
    if (window.matchMedia("(max-width: 1023px)").matches) {
      closeSidebar();
    }
  }, [location.pathname, closeSidebar]);

  const handleLogout = (): void => {
    setIsLogoutLoading(false);
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async (): Promise<void> => {
    setIsLogoutLoading(true);
    try {
      await logout();
    } finally {
      setIsLogoutLoading(false);
      setIsLogoutDialogOpen(false);
      navigate("/login", { replace: true });
    }
  };

  const displayName = getDisplayName(
    user?.firstName,
    user?.lastName,
    user?.displayName,
    user?.email ?? ""
  );

  const currentUserAvatarSrc = useMyProfileAvatarObjectUrl(user?.profileImage);

  const navigationItems = useMemo(() => {
    if (isEnterprise) {
      const items: Array<{
        to: string;
        labelKey: string;
        icon: typeof FileCheck;
        end?: boolean;
      }> = [
        { to: "/", labelKey: "enterpriseStaff.shell.nav.dashboard", icon: LayoutDashboard, end: true },
        { to: "/requests", labelKey: "enterpriseStaff.shell.nav.requests", icon: ClipboardList },
        { to: "/members", labelKey: "enterpriseStaff.shell.nav.members", icon: Users },
      ];
      if (isEnterpriseAdmin) {
        items.push({ to: "/settings", labelKey: "enterpriseStaff.shell.nav.settings", icon: Settings });
      }
      return items;
    }
    if (isAccountant) {
      return [
        { to: "/requests", labelKey: "navigation.requests", icon: FileCheck },
        { to: "/earnings", labelKey: "navigation.professionalEarnings", icon: Wallet },
      ];
    }
    if (isPlatform) {
      const platformItems: Array<{
        to: string;
        labelKey: string;
        icon: typeof FileCheck;
        end?: boolean;
      }> = [
        { to: "/platform/analytics", labelKey: "navigation.analytics", icon: BarChart3 },
        { to: "/platform/enterprises", labelKey: "navigation.enterprises", icon: Building2 },
        { to: "/requests", labelKey: "navigation.requests", icon: FileCheck },
        { to: "/platform/quote-requests", labelKey: "navigation.quoteRequests", icon: ClipboardList },
        /*
          ONE ENTRY (spec 2f0e5645). Two entries were two sections, and a
          third profession would have been a third — in the sidebar, which is
          where a human finds out what this platform thinks exists.
        */
        { to: "/profesionales", labelKey: "navigation.professionals", icon: Stamp },
        { to: "/platform/professions", labelKey: "navigation.professionRegistry", icon: Layers3 },
        { to: "/users", labelKey: "navigation.users", icon: Users },
        {
          to: "/products",
          labelKey: "navigation.products",
          icon: Banknote,
        },
        {
          to: "/certificate-request-intents",
          labelKey: "navigation.certificateRequestIntents",
          icon: Layers,
        },
        {
          to: "/professional-payouts",
          labelKey: "navigation.professionalPayouts",
          icon: CircleDollarSign,
        },
        {
          to: "/moderation-flags",
          labelKey: "navigation.moderationFlags",
          icon: ShieldAlert,
        },
        {
          to: "/platform/bank-transfers",
          labelKey: "navigation.bankTransfers",
          icon: Landmark,
        },
        { to: "/feedback", labelKey: "navigation.feedback", icon: MessageSquare },
        { to: "/ratings", labelKey: "navigation.ratings", icon: Star },
        { to: "/platform/blog", labelKey: "navigation.blog", icon: Newspaper },
      ];
      if (user?.role === "notary") {
        return [
          { to: "/requests", labelKey: "navigation.requests", icon: FileCheck },
          { to: "/earnings", labelKey: "navigation.professionalEarnings", icon: Wallet },
          ...platformItems,
        ];
      }
      return platformItems;
    }
    if (user?.role === "notary" || user?.isNotary) {
      return [
        { to: "/requests", labelKey: "navigation.requests", icon: FileCheck },
        { to: "/earnings", labelKey: "navigation.professionalEarnings", icon: Wallet },
      ];
    }
    // The CONSUMER branch. Addresses are here and not in the professional or
    // platform lists above because this is the only role that picks a delivery
    // address during intake, and therefore the only one accumulating addresses
    // it has no other way to correct.
    return [
      { to: "/requests", labelKey: "navigation.requests", icon: FileCheck },
      { to: "/addresses", labelKey: "navigation.addresses", icon: MapPin },
    ];
  }, [isEnterprise, isEnterpriseAdmin, isAccountant, isPlatform, user?.role, user?.isNotary]);

  return (
    <>
      {isSidebarOpen && (
        <button
          type="button"
          className="fixed inset-x-0 bottom-0 z-30 bg-background/60 backdrop-blur top-14 md:top-16 lg:hidden"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex flex-col border-r bg-background shadow-lg transition-all duration-300 lg:static lg:h-full lg:shadow-none",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
          isSidebarOpen ? "w-64" : "lg:w-16"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-4 z-50 hidden h-8 w-8 translate-x-1/2 rounded-full border bg-background shadow-md transition-all hover:bg-accent hover:text-accent-foreground lg:flex"
          onClick={toggleSidebar}
          aria-label={t("header.toggleNavigation")}
        >
          {isSidebarOpen ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeftOpen className="h-4 w-4" />
          )}
        </Button>
        <div
          className={cn(
            "flex h-16 items-center px-6",
            !isSidebarOpen && "lg:justify-center lg:px-2"
          )}
        >
          <NavLink to="/" className="flex items-center gap-2">
            {isEnterprise && enterprise?.id && enterprise?.logoUrl ? (
              enterprise?.logoUrlDark ? (
                <>
                  <img
                    src={enterpriseLogoUrl(enterprise.id)}
                    alt={enterprise.name}
                    className="h-7 w-auto object-contain dark:hidden"
                  />
                  <img
                    src={enterpriseDarkLogoUrl(enterprise.id)}
                    alt={enterprise.name}
                    className="hidden h-7 w-auto object-contain dark:block"
                  />
                </>
              ) : (
                <img
                  src={enterpriseLogoUrl(enterprise.id)}
                  alt={enterprise.name}
                  className="h-7 w-auto object-contain dark:[filter:brightness(0)_invert(1)]"
                />
              )
            ) : isEnterprise && enterprise?.name ? (
              <span className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                {isSidebarOpen && (
                  <span className="truncate text-sm font-semibold text-foreground">
                    {enterprise.name}
                  </span>
                )}
              </span>
            ) : isSidebarOpen ? (
              <BrandLogo variant="full" imgClassName="h-7 w-auto" />
            ) : (
              <span className="hidden lg:inline-flex">
                <BrandLogo variant="iso" imgClassName="h-7 w-7" />
              </span>
            )}
          </NavLink>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4 text-sm">
          {navigationItems.map((item, index) => {
            const Icon = item.icon;
            const hasMatchedNavigationItem = navigationItems.some((navigationItem) =>
              location.pathname === navigationItem.to ||
              location.pathname.startsWith(`${navigationItem.to}/`)
            );
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end === true : false}
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) => {
                  const isDefaultActive = index === 0 && !hasMatchedNavigationItem;
                  const isItemActive = isActive || isDefaultActive;
                  return cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-muted-foreground transition-colors",
                    isItemActive
                      ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      : "hover:bg-muted hover:text-foreground",
                    !isSidebarOpen && "lg:justify-center lg:px-2"
                  );
                }}
                title={!isSidebarOpen ? t(item.labelKey) : undefined}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {isSidebarOpen && <span>{t(item.labelKey)}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="flex-shrink-0 border-t p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "group min-h-12 w-full gap-2 rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-accent/90 hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  !isSidebarOpen && "lg:justify-center lg:px-2"
                )}
              >
                <UserAvatar
                  size="sm"
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  email={user?.email}
                  displayName={user?.displayName ?? displayName}
                  imageSrc={currentUserAvatarSrc}
                />
                {isSidebarOpen && (
                  <div className="flex flex-1 flex-col items-start overflow-hidden">
                    <span className="truncate text-sm font-medium">
                      {displayName || t("header.unknown")}
                    </span>
                    {user?.email && (
                      <span className="truncate text-xs text-muted-foreground group-hover:text-accent-foreground">
                        {user.email}
                      </span>
                    )}
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={isSidebarOpen ? "start" : "end"}
              side="top"
              className="w-56"
            >
              <DropdownMenuLabel>{t("header.account")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <UserCircle className="mr-2 h-4 w-4" />
                {t("header.profile")}
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  <Moon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 text-left">{t("theme.toggle")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  sideOffset={6}
                  alignOffset={-4}
                  className="min-w-[10rem]"
                >
                  <ThemePreferenceMenuItems />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t("header.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <ConfirmationDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t("header.logoutDialog.title")}
        description={t("header.logoutDialog.description")}
        confirmText={t("header.logoutDialog.confirm")}
        cancelText={t("header.logoutDialog.cancel")}
        loadingText={t("header.logoutDialog.loading")}
        isLoading={isLogoutLoading}
        variant="destructive"
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
}
